import { useSearchParams } from 'next/navigation';
import { InfiniteData, useInfiniteQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseInfiniteQueryAvailableOffersData =
  TravelPortal.Responses.AvailableOffers;

interface UseInfiniteQueryAvailableOffersOptions {
  initialData?: InfiniteData<UseInfiniteQueryAvailableOffersData>;
}

export const useInfiniteQueryAvailableOffers = (
  options?: UseInfiniteQueryAvailableOffersOptions,
) => {
  const searchParams = useSearchParams();
  const locale = getLocale();

  const query = useInfiniteQuery<UseInfiniteQueryAvailableOffersData>(
    [QueryId.AVAILABLE_OFFERS, locale, searchParams!.toString()],
    async ({ pageParam = 0 }) => {
      const baseUrl = '/api/available-offers';

      const searchParameters = new URLSearchParams({
        adultsNumber: String(searchParams!.get('adultsNumber')),
        area: String(searchParams!.get('area')),
        departureDate: String(searchParams!.get('departureDate')),
        durationType: String(searchParams!.get('durationType')),
        skip: pageParam.toString(),
      });

      if (searchParams!.get('batchSize')) {
        searchParameters.append(
          'batchSize',
          String(searchParams!.get('batchSize')),
        );
      }

      if (searchParams!.get('childrenAges')) {
        searchParameters.append(
          'childrenAges',
          String(searchParams!.get('childrenAges')),
        );
      }

      if (searchParams!.get('departureIds')) {
        searchParameters.append(
          'departureIds',
          String(searchParams!.get('departureIds')),
        );
      }

      if (searchParams!.get('countryIds')) {
        searchParameters.append(
          'countryIds',
          String(searchParams!.get('countryIds')),
        );
      }

      if (searchParams!.get('destinationIds')) {
        searchParameters.append(
          'destinationIds',
          String(searchParams!.get('destinationIds')),
        );
      }

      if (searchParams!.get('durationRange')) {
        searchParameters.append(
          'durationRange',
          String(searchParams!.get('durationRange')),
        );
      }

      if (searchParams!.get('filterPricePerPerson')) {
        searchParameters.append(
          'filterPricePerPerson',
          String(searchParams!.get('filterPricePerPerson')),
        );
      }

      if (searchParams!.get('filterProviders')) {
        searchParameters.append(
          'filterProviders',
          String(searchParams!.get('filterProviders')),
        );
      }

      if (searchParams!.get('filterStars')) {
        searchParameters.append(
          'filterStars',
          String(searchParams!.get('filterStars')),
        );
      }

      if (searchParams!.get('filterFacilityIds')) {
        searchParameters.append(
          'filterFacilityIds',
          String(searchParams!.get('filterFacilityIds')),
        );
      }

      if (searchParams!.get('filterFacilityValues')) {
        searchParameters.append(
          'filterFacilityValues',
          String(searchParams!.get('filterFacilityValues')),
        );
      }

      if (searchParams!.get('sortBy')) {
        searchParameters.append('sortBy', String(searchParams!.get('sortBy')));
      }

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useInfiniteQueryAvailableOffers', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled:
        !!searchParams!.get('adultsNumber') &&
        !!searchParams!.get('area') &&
        !!searchParams!.get('departureDate') &&
        !!searchParams!.get('durationType'),
      getNextPageParam: lastPage => {
        const { pagination } = lastPage;
        const totalResults = pagination.count;
        const currentSkip = pagination.skip;
        const currentTake = pagination.take;

        const nextSkip = currentSkip + currentTake;

        if (nextSkip < totalResults) {
          return nextSkip;
        }

        return undefined;
      },
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};
